/** ICONURILE DE INTERFAȚĂ ale tronsonului — un singur alfabet, într-un fișier.
 *
 *  DE CE NU EMOJI. Bara de sus avea `⊞`, `🅰`, `🔓`, `🪄`, `↖`, `🖌`: șase semne
 *  din șase familii diferite, fiecare cu greutatea, culoarea și linia de bază
 *  ale fontului de emoji al sistemului — pe macOS 🔓 e galben și lucios, 🅰 e o
 *  pastilă albastră, iar `⊞` e o linie subțire de font matematic. Puse pe
 *  același rând arătau ca o grămadă de autocolante, nu ca o bară de unelte, și
 *  nu se puteau colora: un emoji nu ascultă de `currentColor`, deci butonul
 *  aprins rămânea cu semnul lui de dinainte.
 *
 *  Aici sunt desene de linie: `currentColor`, aceeași grosime, aceeași cutie.
 *  Se aprind cu butonul, se sting cu el și se citesc la fel la 16 și la 20 px.
 *
 *  ACEEAȘI LINIE CA GLIFELE PALETEI (`editor/Paleta.tsx`, componenta `G`):
 *  grosime 1,4–1,5, capete și colțuri rotunde, fără umplutură. Alea desenează
 *  simboluri de planșă la 34×26, astea desenează unelte la 20×20 — două mărimi,
 *  un singur ton.
 */

/** Numele fiecărui icon. Căile sunt scrise în cutia 20×20, bucățile separate
 *  cu `|` — aceeași convenție ca la glifele paletei. */
const CAI = {
  /** ‹ — înapoi, în capul barei. */
  inapoi: "M12 4.5 6.5 10 12 15.5",
  /** Chevron spre stânga / spre dreapta — strânsul paletei. */
  stanga: "M11.5 5 6.5 10l5 5",
  dreapta: "M8.5 5l5 5-5 5",
  /** …și în jos: capul unei grupe desfăcute (v. `Paleta`). */
  jos: "M5 8.5l5 5 5-5",
  /** Axele de trasare: caroiajul. */
  axe: "M3 7.5h14|M3 12.5h14|M7.5 3v14|M12.5 3v14",
  /** Cotele: linia de cotă cu cele două capete. */
  cote: "M3 10h14|M3 7v6|M17 7v6|M5.5 11.5 7 8.5|M13 11.5l1.5-3",
  /** Apartamentele: două încăperi alăturate, una spălată. */
  apartamente: "M3 4.5h14v11H3z|M10 4.5v11|M4.5 7h4|M4.5 9.5h4|M4.5 12h4",
  /** Numele și ariile camerelor: scris într-o încăpere. */
  etichete: "M3 4.5h14v11H3z|M6 9h8|M6 12h5",
  /** Pardoseala: dalele ei, în perspectivă — cum se desenează o podea, nu cum
      se desenează un caroiaj (ăla e deja `axe`, și două caroiaje alăturate în
      aceeași bară nu s-ar deosebi). */
  pardoseli: "M2.5 15.5 6.5 5.5h7l4 10Z|M4.7 10.5h10.6|M8.2 5.5 6.5 15.5|M11.8 5.5l1.7 10",
  /** The electrical layer: the plan symbol of a socket, so the button reads as
      the layer it toggles. */
  electrice: "M4.5 11.5a5.5 5.5 0 0 1 11 0|M3 11.5h14|M10 11.5V7.5",
  /** The view layers: three sheets stacked, the way a layer list is drawn. */
  layere: "M10 3 17.5 7 10 11 2.5 7Z|M2.5 10.5 10 14.5l7.5-4|M2.5 14 10 18l7.5-4",
  /** Bagheta: amenajarea automată. */
  bagheta: "M3.5 16.5 11 9|M9.5 7.5l3 3|M14.8 2.8v3.4|M13.1 4.5h3.4|M17 9.6v2.2|M15.9 10.7h2.2",
  /** Săgeata de selecție. */
  sageata: "M5.5 3 5.5 15.4 8.7 12.2 10.9 16.8 13 15.8 10.9 11.3 15.2 10.9Z",
  /** Pensula de finisaje. */
  pensula: "M14.4 3.4a2.2 2.2 0 0 1 3.1 3.1L10 14l-3.6.6.6-3.6z|M12.4 5.4l3.1 3.1",
  /** Hârtia de calc: o imagine pusă sub mână — ramă, orizont, soare. */
  calc: "M3 4.5h14v11H3z|M3 12.6l4-3.6 3.2 2.9L14 8.1l3 3|M7.4 7.6a1.1 1.1 0 1 1-2.2 0 1.1 1.1 0 0 1 2.2 0",
  /** Crucea care închide o bandă. */
  inchide: "M5.5 5.5l9 9|M14.5 5.5l-9 9",
  /** Mărirea. */
  minus: "M5 10h10",
  plus: "M10 5v10|M5 10h10",
  /** Încadrarea pe tot desenul. */
  incadreaza: "M4 7.5V4h3.5|M12.5 4H16v3.5|M16 12.5V16h-3.5|M7.5 16H4v-3.5",
  /** Oglindirea pe orizontală și pe verticală. */
  oglindaX: "M10 3v14|M6.6 6.8 3.2 10l3.4 3.2|M13.4 6.8 16.8 10l-3.4 3.2",
  oglindaY: "M3 10h14|M6.8 13.4 10 16.8l3.2-3.4|M6.8 6.6 10 3.2l3.2 3.4",
  /** Sfertul de tur, într-o parte și în cealaltă: un arc de trei sferturi cu
      săgeata la capăt. Perechea se citește din SENSUL arcului, nu dintr-un semn
      scris lângă el — o bară de unelte se citește din formă, la 15 px. */
  rotireStanga: "M4.2 10a5.8 5.8 0 1 1 1.9 4.3|M4.2 6.4v3.8h3.8",
  rotireDreapta: "M15.8 10a5.8 5.8 0 1 0-1.9 4.3|M15.8 6.4v3.8h-3.8",
  /** Fațada: un bloc cu ferestre și ușa de la parter — regulile fațadei. */
  fatada: "M4.5 17V3.5h11V17|M2.5 17h15|M7 6.5h2|M11 6.5h2|M7 10h2|M11 10h2|M8.8 17v-3.5h2.4V17",
  /** Soarele — însorirea fațadelor. */
  soare: "M10 6.6a3.4 3.4 0 1 0 0 6.8a3.4 3.4 0 1 0 0-6.8|M10 2v2|M10 16v2|M2 10h2|M16 10h2|M4.3 4.3l1.4 1.4|M14.3 14.3l1.4 1.4|M15.7 4.3l-1.4 1.4|M5.7 14.3l-1.4 1.4",
  /** Soarele deasupra orizontului — lumina și umbrele machetei, cu rigla orei. */
  lumina: "M2.5 15.5h15|M6 15.5a4 4 0 0 1 8 0|M10 5v2|M4.6 8.4l1.4 1.3|M15.4 8.4 14 9.7|M2.5 12.3h1.8|M15.7 12.3h1.8",
  /** Harta lotului, pliată — planul de situație sub nivel. */
  sit: "M2.5 5.5l5-2 5 2 5-2v11l-5 2-5-2-5 2z|M7.5 3.5v11|M12.5 5.5v11",
  /** Orașul din jurul lotului: trei volume de înălțimi diferite pe o linie de teren. */
  oras: "M2.5 17h15|M3.5 17V9.5h4V17|M7.5 17V4.5h5V17|M12.5 17v-6h4v6",
  /** Zona interzisă: un contur hașurat. */
  interzis: "M3 3h14v14H3z|M3 9l6-6|M3 15L15 3|M9 17l8-8",
  /** o canapea văzută de sus: spătarul și cele două brațe */
  mobilier: "M3.5 8.5h13v6h-13z|M3.5 8.5V6a1.5 1.5 0 0 1 3 0v2.5|M16.5 8.5V6a1.5 1.5 0 0 0-3 0v2.5|M7 8.5v6|M13 8.5v6",
  /** Semnele de circulație: triunghiul «cedează», cu vârful în jos. */
  cedeaza: "M3 4.5h14L10 16Z|M6.6 7.2h6.8L10 13Z",
  /** A padlock: a field that follows another value and is not typed. */
  lacat: "M5 9h10v8H5z|M7 9V6.5a3 3 0 0 1 6 0V9|M10 12.2v2",
} as const;

export type NumeIcoana = keyof typeof CAI;

export function Icoana({ nume, marime = 16 }: {
  nume: NumeIcoana;
  /** Latura cutiei, în pixeli. Implicit 16 — mărimea unui icon lângă text. */
  marime?: number;
}) {
  return (
    <svg viewBox="0 0 20 20" width={marime} height={marime} fill="none"
         stroke="currentColor" strokeWidth={1.5} strokeLinecap="round"
         strokeLinejoin="round" aria-hidden focusable="false">
      {CAI[nume].split("|").map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}
